/**
 * The newsletter signup module.
 *
 * Extracted from BlogPost so the same form can sit on the article, the blog
 * index and anywhere else worth asking — previously it existed once, inside
 * one article template, and its CSS was scoped under `article` so it could not
 * be reused anywhere else.
 *
 * Three behaviors the old inline version got wrong:
 *   - It flipped to "You're in" before either request resolved, so a total
 *     backend outage still looked like success. This awaits the response.
 *   - It had no error state at all. This one says what to do next.
 *   - It promised "you're in" on a flow that now needs a confirmation click,
 *     so the copy says to check the inbox instead.
 */
import { useState } from "react";
import { END_OF_ARTICLE } from "@/lib/blogCta";

type Status = "idle" | "submitting" | "done" | "error";

export default function NewsletterForm({
  source,
  idPrefix = "newsletter",
  theme = "light",
}: {
  /** Blog post slug, or the page the form sits on — stored as signup provenance. */
  source: string;
  /** Keeps input ids unique when two instances share a page. */
  idPrefix?: string;
  /**
   * Article pages are white, the blog index is near-black. Both palettes are
   * defined as tokens below so the module sits on either ground without a
   * second copy of the markup.
   */
  theme?: "light" | "dark";
}) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // Honeypot — never shown to people.
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  const inputId = `${idPrefix}-email`;
  const headingId = `${idPrefix}-heading`;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || status === "submitting") return;

    setStatus("submitting");
    setError("");
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source,
          company,
          pageUrl: typeof window !== "undefined" ? window.location.href : null,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (!res.ok || !data.success) {
        setError(data.error || "Something went wrong. Please try again.");
        setStatus("error");
        return;
      }
      setStatus("done");
    } catch {
      setError("We couldn't reach the server. Check your connection and try again.");
      setStatus("error");
    }
  }

  return (
    <section className={`nl-module nl-${theme}`} aria-labelledby={headingId}>
      <p className="nl-label">{END_OF_ARTICLE.label}</p>
      <h2 id={headingId} className="nl-heading">{END_OF_ARTICLE.heading}</h2>
      <p className="nl-body">{END_OF_ARTICLE.body}</p>

      {status === "done" ? (
        <p className="nl-done" role="status">
          Check your inbox — click the link in the confirmation email and you're on the list.
        </p>
      ) : (
        <>
          <form onSubmit={onSubmit} className="nl-form" noValidate>
            <label htmlFor={inputId} className="sr-only">Email address</label>
            <input
              id={inputId}
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "submitting"}
              required
            />
            {/* Honeypot. Hidden from people and from screen readers; bots fill it in. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="nl-hp"
            />
            <button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
          {status === "error" && (
            <p className="nl-error" role="alert">{error}</p>
          )}
        </>
      )}

      <p className="nl-fine">One email a week. Unsubscribe in one click.</p>

      <style>{`
        .nl-light {
          --nl-bg:#f7fbfb; --nl-border:rgba(21,127,139,0.22); --nl-accent:#157f8b;
          --nl-accent-hover:#0d5a63; --nl-heading:#111; --nl-body:#42494b;
          --nl-input-bg:#fff; --nl-input-border:rgba(0,0,0,0.2); --nl-input-text:#15191a;
          --nl-placeholder:#7b8688; --nl-btn-text:#fff; --nl-fine:#5f6b6d;
          --nl-done:#157f8b; --nl-error:#b3261e;
        }
        .nl-dark {
          --nl-bg:rgba(87,215,226,0.05); --nl-border:rgba(87,215,226,0.28); --nl-accent:#57d7e2;
          --nl-accent-hover:#7ee3ec; --nl-heading:#fff; --nl-body:rgba(255,255,255,0.72);
          --nl-input-bg:rgba(255,255,255,0.06); --nl-input-border:rgba(255,255,255,0.18); --nl-input-text:#fff;
          --nl-placeholder:rgba(255,255,255,0.4); --nl-btn-text:#0c0c0c; --nl-fine:rgba(255,255,255,0.45);
          --nl-done:#57d7e2; --nl-error:#ff9d94;
        }
        .nl-module {
          margin:48px 0 0; padding:28px 30px;
          background:var(--nl-bg);
          border:1px solid var(--nl-border);
          border-left:3px solid var(--nl-accent);
          border-radius:0 10px 10px 0;
        }
        .nl-label {
          color:var(--nl-accent); font-size:11px; font-weight:800;
          letter-spacing:0.12em; text-transform:uppercase;
          margin:0 0 8px; font-family:'Barlow',sans-serif;
        }
        .nl-heading {
          color:var(--nl-heading); font-size:23px; font-weight:800;
          font-family:'Barlow',sans-serif; margin:0 0 8px;
          line-height:1.15; letter-spacing:-0.02em;
        }
        .nl-body {
          color:var(--nl-body); font-size:15px;
          margin:0 0 18px; line-height:1.55; max-width:54ch;
          font-family:'Barlow',sans-serif;
        }
        .nl-form { display:flex; gap:10px; flex-wrap:wrap; max-width:460px; }
        .nl-form input[type="email"] {
          flex:1; min-width:200px; padding:11px 14px;
          background:var(--nl-input-bg); border:1px solid var(--nl-input-border);
          border-radius:8px; color:var(--nl-input-text); font-size:15px;
          font-family:'Barlow',sans-serif; outline:none;
        }
        .nl-form input[type="email"]::placeholder { color:var(--nl-placeholder); }
        .nl-form input[type="email"]:focus-visible { border-color:var(--nl-accent); box-shadow:0 0 0 3px rgba(21,127,139,0.15); }
        .nl-form input[type="email"]:disabled { opacity:0.6; }
        /* Off-screen rather than display:none — a hidden field some bots skip. */
        .nl-hp { position:absolute; left:-9999px; width:1px; height:1px; opacity:0; }
        .nl-form button {
          padding:11px 22px; background:var(--nl-accent); border:none; border-radius:8px;
          color:var(--nl-btn-text); font-size:15px; font-weight:700;
          font-family:'Barlow',sans-serif; cursor:pointer; flex-shrink:0;
          transition:background 0.15s;
        }
        .nl-form button:hover:not(:disabled) { background:var(--nl-accent-hover); }
        .nl-form button:disabled { opacity:0.65; cursor:default; }
        .nl-done { color:var(--nl-done); font-size:16px; font-weight:700; margin:0; font-family:'Barlow',sans-serif; line-height:1.5; }
        .nl-error { color:var(--nl-error); font-size:14px; font-weight:600; margin:12px 0 0; font-family:'Barlow',sans-serif; line-height:1.5; }
        .nl-fine { color:var(--nl-fine); font-size:12px; margin:14px 0 0; font-family:'Barlow',sans-serif; }
        @media (max-width:768px) {
          .nl-module { padding:26px 22px; }
          .nl-form button { width:100%; }
        }
      `}</style>
    </section>
  );
}
